import React, { useState, useEffect, useRef, use } from 'react';
import {
  Typography,
  Button,
  Box,
  Modal,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListSubheader,
  IconButton,
  FormHelperText,
  Switch,
} from '@mui/material';
import { Handle, Position } from 'reactflow';
import { ExcelToJsonConverter, generateFilterKey } from '../../../../utils/helper';
import {
  botFlowDataAtom,
  entityPreviewDataAtom,
  flowObjAtom,
  flowValidationAtom,
} from '../../../../Services/_states';
import { useRecoilState, useRecoilValue, useRecoilValueLoadable } from 'recoil';
import RenderTextArea from './RenderTextArea.tsx';
import ModalForms from '../../../Shared/Modals/index.js';
import EntityDropdown from '../../../Shared/EntityDropdown/EntityDropdown.tsx';
import RenderNestedList from '../../../Shared/RenderNestedList/index.tsx';
import { entityDetailsSelectorFamily } from '../../../../Services/_states/Entity/entityDetailsSelector.ts';
import { EntityActions } from '../../../../Services/_actions/Entity/entity.actions.ts';
import SearchableDropdown from '../../Common/SearchableDropdown/SearchableDropdown.tsx';
import EntityDataSetting from '../EntityDataSetting.tsx';
import {
  EntitySetting,
  EntityValidationErrors,
  LeadDataValidationErrors,
} from '../../../../interfaces/Flowbuilder.interface.ts';
import CommonBadges from '../../../Shared/Badges/index.jsx';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import LeadDataSetting from '../LeadDataSetting.tsx';
import {
  BLOCKED_EXTENSIONS,
  WHATSAPP_ALLOWED_MIME,
  WHATSAPP_LIMITS,
} from '../../../Whatsapp/Template/template.constant.js';
import { useSnackBar } from '../../Common/SnackBar/index.tsx';
import FileInput from '../../../Shared/FileUploadPreview/FileInput.jsx';
import { getAllParentNodeResults } from '../../helper.tsx';

const sources = ['Custom', 'Entity', 'Leads', 'File Upload'];

const DynamicOptionsNode = ({ data }: any) => {
  let totalOptionsLength = data?.options?.reduce((sum, item) => sum + item?.options.length, 0);
  const flowValidationObj = useRecoilValue(flowValidationAtom);
  const [fileName, setFileName] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isEntityModalOpen, setIsEntityModalOpen] = useState<boolean>(false);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState<boolean>(false);
  const [fileOptions, setFileOptions] = useState<string[]>([]);
  const [selectedFileOptions, setSelectedFileOptions] = useState<string[]>([]);
  const [bodyText, setBodyText] = useState(data?.body || '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [entitySetting, setEntitySetting] = useState<EntitySetting>({
    entity: null,
    operator: 'AND',
    type: 'group',
    id: `${data?.id}_${Date.now()}`,
    retrive_fields: [],
    conditions: [],
    subGroups: [],
  });
  const [entitySettingErrors, setEntitySettingErrors] = useState<any>({});
  const [leadDataObj, setLeadDataObj] = useState({
    id: `${data?.id}_${Date.now()}`,
    operator: 'AND',
    type: 'group',
    campaign: null,
    webForm: null,
    conditions: [],
    subGroups: [],
    retrive_fields: [],
  });
  const [leadDataSettingErrors, setLeadDataSettingErrors] = useState<any>({});
  const flowObj = useRecoilValue(flowObjAtom);

  const parentResults = getAllParentNodeResults(data?.id ?? null, flowObj);
  useEffect(() => {
    if (data.body !== bodyText) {
      setBodyText(data.body || '');
    }
  }, [data.body]);

  useEffect(() => {
    if (!data?.sourceType) {
      data.onChange(data.id, {
        sourceType: 'Custom',
        options: [{ title: '', options: [''] }],
        selectedEntityForm: null,
      });
    }
  }, []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 12}px`;
    }
  }, [bodyText]);

  const inferDocType = (mime: string): 'image' | 'video' | 'audio' | 'file' | null => {
    if (WHATSAPP_ALLOWED_MIME.image.includes(mime)) return 'image';
    if (WHATSAPP_ALLOWED_MIME.video.includes(mime)) return 'video';
    if (WHATSAPP_ALLOWED_MIME.audio.includes(mime)) return 'audio';
    if (WHATSAPP_ALLOWED_MIME.file.includes(mime)) return 'file';
    return null;
  };
  const bytesToMB = (bytes: number) => (bytes / (1024 * 1024)).toFixed(2);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      const fileName = file.name.toLowerCase();
      const ext = fileName.split('.').pop();

      if (BLOCKED_EXTENSIONS.has(ext)) {
        showSnackBar(`"${ext}" files are not allowed for security reasons.`, 'error');
        event.target.value = '';
        return;
      }

      const typeGroup = inferDocType(file.type);
      if (!typeGroup) {
        showSnackBar(`Unsupported file type (${file.type || ext}).`, 'error');
        event.target.value = '';
        return;
      }

      const limit = WHATSAPP_LIMITS[typeGroup];
      if (file.size > limit) {
        showSnackBar(
          `File too large for ${typeGroup}. Max ${bytesToMB(limit)} MB. Your file is ${bytesToMB(file.size)} MB.`,
          'error'
        );
        event.target.value = '';
        return;
      }
      const fileData: any = await ExcelToJsonConverter(file);
      // const arr = fileData?.map((item:any) => item?.Option);
      setFileOptions(fileData);
      setSelectedFileOptions([]);
      setIsOpen(true);
      setFileName(file.name);
    }
  };

  const toggleFileOption = (option: string) => {
    setSelectedFileOptions((prev) => {
      if (prev.includes(option)) {
        return prev.filter((item) => item !== option);
      } else if (prev.length < 10) {
        return [...prev, option];
      }
      return prev; // Return the same list if limit exceeded
    });
  };

  const handleSave = () => {
    const updatedOptions = [...selectedFileOptions];
    data.onChange(data.id, { options: updatedOptions });
    setIsOpen(false); // Close the modal
  };

  const validateEntitySetting = (entityData: any): EntityValidationErrors => {
    const errors: EntityValidationErrors = {};
    if (!entityData.entity?._id) {
      errors.entity = 'Entity ID is missing';
    }
    if (!Array.isArray(entityData?.query_parameter) || entityData.query_parameter.length === 0) {
      errors.query_parameter = { 0: { param: 'At least one query_parameter is required' } };
    } else {
      entityData.query_parameter.forEach((qp: any, index: number) => {
        if (!qp.param?.value) {
          errors.query_parameter = {
            ...(errors.query_parameter || {}),
            [index]: {
              ...(errors.query_parameter?.[index] || {}),
              param: 'param is missing',
            },
          };
        }
        if (!qp.matched_with?.name) {
          errors.query_parameter = {
            ...(errors.query_parameter || {}),
            [index]: {
              ...(errors.query_parameter?.[index] || {}),
              matched_with: 'matched_with.name is missing',
            },
          };
        }
      });
    }
    // validate retrive_fields
    if (!Array.isArray(entityData?.retrive_fields) || entityData?.retrive_fields.length === 0) {
      errors.retrive_fields = 'At least one retrive_field is required';
    }
    return errors;
  };
  const handleSaveEntitySettings = () => {
    // const errors: EntityValidationErrors = validateEntitySetting(entitySetting);
    // if (Object.keys(errors).length === 0) {
    setIsEntityModalOpen(false);
    data.onChange(data.id, {
      entity_setting: entitySetting,
      lead_data_setting: null,
    });
    // } else {
    //   setEntitySettingErrors(errors);
    // }
  };

  const validateLeadDataSetting = (leadData: any): LeadDataValidationErrors => {
    const errors: LeadDataValidationErrors = {};
    if (!leadData?.campaign?.proposal_id) {
      errors.campaign = 'Campaign ID is missing';
    }
    if (!leadData?.webForm?.leads_form_id) {
      errors.webForm = 'WebForm ID is missing';
    }
    if (!Array.isArray(leadData?.query_parameter) || leadData.query_parameter.length === 0) {
      errors.query_parameter = { 0: { param: 'At least one query_parameter is required' } };
    } else {
      leadData.query_parameter.forEach((qp: any, index: number) => {
        if (!qp.param?.value && !qp?.param?.status_id && !qp?.param?.name) {
          errors.query_parameter = {
            ...(errors.query_parameter || {}),
            [index]: {
              ...(errors.query_parameter?.[index] || {}),
              param: 'param is missing',
            },
          };
        }
        if (!qp.matched_with?.key_name) {
          errors.query_parameter = {
            ...(errors.query_parameter || {}),
            [index]: {
              ...(errors.query_parameter?.[index] || {}),
              matched_with: 'matched_with.key_name is missing',
            },
          };
        }
      });
    }
    // validate retrive_fields
    if (!Array.isArray(leadData?.retrive_fields) || leadData?.retrive_fields.length === 0) {
      errors.retrive_fields = 'At least one retrive_field is required';
    }
    return errors;
  };

  const handleSaveLeadDataSettings = () => {
    // const errors: LeadDataValidationErrors = validateLeadDataSetting(leadDataObj);
    // setLeadDataSettingErrors(errors);
    // if (Object?.keys(errors)?.length === 0) {
    setIsLeadModalOpen(false);
    data.onChange(data.id, {
      entity_setting: null,
      lead_data_setting: leadDataObj,
    });
    // }
  };

  useEffect(() => {
    if (data?.entity_setting) {
      setEntitySetting(data?.entity_setting);
    } else if (data?.lead_data_setting) {
      setLeadDataObj(data?.lead_data_setting);
    }
  }, []);

  return (
    <div>
      <Box mb={2}>
        <FormControlLabel
          control={
            <Switch
              checked={data.ai_mode_enabled ?? false}
              onChange={(e) => {
                const enabled = e.target.checked;

                if (!enabled) {
                  data.onChange(data.id, { ai_mode_enabled: false, ai_query_parameters: [] });
                } else {
                  data.onChange(data.id, { ai_mode_enabled: true });
                }
              }}
              color="primary"
            />
          }
          label="AI"
          labelPlacement="end"
        />
        {data.ai_mode_enabled && (
          <SearchableDropdown
            options={parentResults.map((item) => ({
              label: item.label,
              value: item.value,
            }))}
            multiSelect={true}
            onChange={(selectedOptions) => {
              const selectedValues = (selectedOptions as any[]).map((opt) => opt.value);
              data.onChange(data.id, { ai_query_parameters: selectedValues });
            }}
            value={parentResults
              .map((item) => ({ label: item.label, value: item.value }))
              .filter((item) => (data.ai_query_parameters || []).includes(item.value))}
          />
        )}
      </Box>
      <div style={{ marginBottom: '10px', position: 'relative' }}>
        <label>Header:</label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={data.header || ''}
            onChange={(e) => {
              const newHeader = e.target.value.slice(0, 60);
              data.onChange(data.id, { header: newHeader });
            }}
            placeholder="Enter Header Text"
            style={{
              width: '100%',
              padding: '5px',
              paddingRight: '50px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              background: '#f5f5f5',
            }}
          />
          <span
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '12px',
              color: data.header?.length === 60 ? 'red' : '#888',
            }}
          >
            {`${(data.header || '').length}/60`}
          </span>
        </div>
        {flowValidationObj[data?.id]?.header && (
          <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
            {flowValidationObj[data?.id]?.header}
          </Typography>
        )}
      </div>
      <RenderTextArea data={data} />
      {flowValidationObj[data?.id]?.body && (
        <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
          {flowValidationObj[data?.id]?.body}
        </Typography>
      )}

      {/* )} */}
      <div style={{ marginBottom: '10px', position: 'relative' }}>
        <label>Footer:</label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={data.footer || ''}
            onChange={(e) => {
              const newFooter = e.target.value.slice(0, 60);
              data.onChange(data.id, { footer: newFooter });
            }}
            placeholder="Enter Footer Text"
            style={{
              width: '100%',
              padding: '5px',
              paddingRight: '50px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              background: '#f5f5f5',
            }}
          />
          <span
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '12px',
              color: data.footer?.length === 60 ? 'red' : '#888',
            }}
          >
            {`${(data.footer || '').length}/60`}
          </span>
        </div>
        {flowValidationObj[data?.id]?.footer && (
          <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
            {flowValidationObj[data?.id]?.footer}
          </Typography>
        )}
      </div>

      <div style={{ marginBottom: '10px' }}>
        <label>Data Source:</label>
        <select
          value={data?.sourceType ?? 'Custom'}
          onChange={(e) => {
            const newSource = e.target.value;
            if (newSource === 'Entity') {
              setIsEntityModalOpen(true);
            } else if (newSource === 'Custom') {
              data.onChange(data.id, {
                sourceType: newSource,
                options: [{ title: '', options: [''] }],
                entity_setting: null,
              });
            } else if (newSource === 'Leads') {
              setIsLeadModalOpen(true);
            }
            setLeadDataObj({
              id: `${data?.id}_${Date.now()}`,
              operator: 'AND',
              type: 'group',
              campaign: null,
              webForm: null,
              conditions: [],
              subGroups: [],
              retrive_fields: [],
            });
            setEntitySetting({
              entity: null,
              operator: 'AND',
              type: 'group',
              id: `${data?.id}_${Date.now()}`,
              retrive_fields: [],
              conditions: [],
              subGroups: [],
            });
            data.onChange(data.id, {
              sourceType: newSource,
              lead_data_setting: null,
              entity_setting: null,
            });
          }}
          style={{ width: '100%', padding: '5px', borderRadius: '4px' }}
        >
          <option value="">Select Source</option>
          {sources?.map((source, index) => (
            <option key={index} value={source}>
              {source}
            </option>
          ))}
        </select>
      </div>
      {/* {selectedSource === 'File Upload' && ( */}
      {data?.sourceType === 'File Upload' && (
        <div>
          {/* <input
            type="file"
            accept=".txt,.csv,.xlsx"
            onChange={handleFileUpload}
            style={{ marginTop: '10px' }}
          /> */}
          <FileInput onFileSelect={handleFileUpload} accept=".txt,.csv,.xlsx" />
          <FormHelperText sx={{ mt: 0 }}>Allowed: xlsx,txt ,csv• Max 100 MB</FormHelperText>
          {fileName && <small>Uploaded File: {fileName}</small>}
        </div>
      )}
      {/* {selectedSource === 'Custom' && <ButtonNode data={data} />} */}
      {data?.sourceType === 'Custom' && (
        <div>
          <label>Options (Max 10):</label>
          <div>
            {(data?.options || [])?.map(
              (optionGroup: { title: string; options: string[] }, groupIndex: number) => (
                <div
                  key={groupIndex}
                  style={{ border: '2px solid #d8d8df', marginBottom: '24px', padding: '8px' }}
                >
                  {/* Render the title dynamically */}
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', paddingBottom: '10px' }}>
                      <input
                        type="text"
                        value={optionGroup?.title || ''}
                        onChange={(e) => {
                          const newValue = e.target.value.slice(0, 24);
                          const updatedOptions = [...(data.options || [])];
                          let updateObj = { ...updatedOptions[groupIndex], title: newValue };
                          updatedOptions[groupIndex] = { ...updateObj };
                          data.onChange(data.id, { options: updatedOptions });
                        }}
                        placeholder={`Title`}
                        style={{
                          flex: 1,
                          padding: '5px 35px 5px 5px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          background: '#f5f5f5',
                          height: '40px',
                        }}
                      />
                      {data?.options?.length > 1 && (
                        <span
                          onClick={() => {
                            const updatedOptions = [...(data?.options || [])];
                            updatedOptions.splice(groupIndex, 1);
                            data.onChange(data.id, { options: updatedOptions });
                          }}
                          style={{
                            color: 'red',
                            cursor: 'pointer',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            marginLeft: '10px',
                          }}
                        >
                          &times;
                        </span>
                      )}
                    </div>
                    {flowValidationObj[data?.id]?.groupErrors &&
                      flowValidationObj[data?.id]?.groupErrors[groupIndex] && (
                        <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
                          {flowValidationObj[data?.id]?.groupErrors[groupIndex]}
                        </Typography>
                      )}
                  </>
                  {/* Render the options dynamically */}
                  {optionGroup?.options?.map((optionLabel: string, index: number) => (
                    <>
                      <div
                        key={`${groupIndex}-${index}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '5px',
                          position: 'relative',
                        }}
                      >
                        <div style={{ position: 'relative', flex: 1 }}>
                          <input
                            type="text"
                            value={optionLabel || ''}
                            onChange={(e) => {
                              const newValue = e.target.value.slice(0, 24);
                              const updatedOptions = [...(data.options || [])];
                              updatedOptions[groupIndex] = {
                                ...updatedOptions[groupIndex],
                                options: [...updatedOptions[groupIndex].options],
                              };
                              updatedOptions[groupIndex].options[index] = newValue;
                              data.onChange(data.id, { options: updatedOptions });
                            }}
                            placeholder={`Option ${index + 1}`}
                            style={{
                              width: '100%',
                              padding: '5px 35px 5px 5px',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              background: '#f5f5f5',
                              height: '40px',
                            }}
                          />
                          <span
                            style={{
                              position: 'absolute',
                              right: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              fontSize: '12px',
                              color: optionLabel.length === 24 ? 'red' : '#888',
                            }}
                          >
                            {`${(optionLabel || '').length}/24`}
                          </span>
                        </div>

                        {/* Connection Handle for Each Button */}
                        <Handle
                          type="source"
                          position={Position.Right}
                          id={`${data.id}-options-${groupIndex}-${index}`}
                          style={{
                            background: '#555',
                            height: '10px',
                            width: '10px',
                            borderRadius: '50%',
                            position: 'absolute',
                            right: '-20px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                          }}
                          onClick={() => data.onOutputClick?.(data.id)}
                        />

                        {/* Remove Button Option */}
                        {optionGroup?.options?.length > 1 && (
                          <span
                            onClick={() => {
                              const updatedOptions = [...(data.options || [])]; // Create a shallow copy of the options array

                              // Create a new group object with a copy of the options array, ensuring immutability
                              updatedOptions[groupIndex] = {
                                ...updatedOptions[groupIndex], // Copy the group object
                                options: updatedOptions[groupIndex].options.filter(
                                  (_, i) => i !== index
                                ), // Filter the options
                              };

                              data.onChange(data.id, { options: updatedOptions }); // Call the onChange function with the updated options
                            }}
                            style={{
                              color: 'red',
                              cursor: 'pointer',
                              marginLeft: '10px',
                              fontSize: '18px',
                              fontWeight: 'bold',
                            }}
                          >
                            &times;
                          </span>
                        )}
                      </div>
                      {flowValidationObj[data?.id]?.optionErrors &&
                        flowValidationObj[data?.id]?.optionErrors &&
                        flowValidationObj[data?.id]?.optionErrors &&
                        flowValidationObj[data?.id]?.optionErrors[groupIndex] &&
                        flowValidationObj[data?.id]?.optionErrors[groupIndex][index] && (
                          <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
                            {flowValidationObj[data?.id]?.optionErrors[groupIndex][index]}
                          </Typography>
                        )}
                    </>
                  ))}

                  {/* Add More Options Button */}
                  {totalOptionsLength < 10 && (
                    <Typography
                      variant="body2"
                      style={{
                        marginTop: '10px',
                        cursor: 'pointer',
                        color: '#007bff',
                        textDecoration: 'underline',
                        fontSize: '14px',
                      }}
                      onClick={() => {
                        // Spread operator to create a shallow copy of the options array
                        const updatedOptions = [...(data.options || [])];

                        // Ensure the groupIndex exists and push a new option into the group's options
                        if (updatedOptions[groupIndex]) {
                          updatedOptions[groupIndex] = {
                            ...updatedOptions[groupIndex], // Clone the option group object
                            options: [...updatedOptions[groupIndex].options, ''], // Add an empty option
                          };
                          data.onChange(data.id, { options: updatedOptions });
                        } else {
                          console.error('Invalid groupIndex');
                        }
                      }}
                    >
                      + Add Option
                    </Typography>
                  )}
                </div>
              )
            )}
          </div>
          {totalOptionsLength < 10 && (
            <Typography
              variant="body2"
              style={{
                marginTop: '10px',
                cursor: 'pointer',
                color: '#007bff',
                textDecoration: 'underline',
                fontSize: '14px',
              }}
              onClick={() => {
                const updatedOptions = [...(data.options || []), { title: '', options: [''] }];
                data.onChange(data.id, { options: updatedOptions });
              }}
            >
              + Add Title
            </Typography>
          )}
        </div>
      )}
      {(data?.sourceType === 'Entity' || data?.sourceType === 'Leads') && (
        <>
          {data?.sourceType === 'Entity' ? (
            <CommonBadges
              value={entitySetting?.entity?.name ?? 'Entity'}
              variant="outlined"
              color="success"
              icon={
                <IconButton
                  onClick={() => {
                    setIsEntityModalOpen(true);
                  }}
                >
                  <ModeEditIcon />
                </IconButton>
              }
            />
          ) : (
            <CommonBadges
              value={leadDataObj?.campaign?.name ?? leadDataObj?.campaign?.name ?? 'Leads'}
              variant="outlined"
              color="success"
              icon={
                <IconButton
                  onClick={() => {
                    setIsLeadModalOpen(true);
                  }}
                >
                  <ModeEditIcon />
                </IconButton>
              }
            />
          )}
          <Handle
            type="source"
            position={Position.Right}
            id={`${data.id}-options-${data?.sourceType}`}
            style={{
              background: '#555',
              height: '10px',
              width: '10px',
              borderRadius: '50%',
              position: 'absolute',
              right: '-20px',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
            onClick={() => data.onOutputClick(data.id)}
          />
        </>
      )}
      <ModalForms
        show={isOpen}
        handleClose={() => setIsOpen(false)}
        modalHeader={'File Options'}
        size="md"
        modalBody={
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: 1,
              maxHeight: '60vh',
            }}
          >
            <List>
              {fileOptions?.map((item: any, index: number) => {
                const label = `${item?.Title}: ${item?.Option}`;
                return (
                  <ListItem
                    key={index}
                    sx={{
                      borderBottom: '1px solid #f0f0f0',
                      '&:last-child': { borderBottom: 'none' },
                      '&:hover': { bgcolor: '#f9f9f9' },
                    }}
                  >
                    <ListItemText primary={label} />
                    <ListItemSecondaryAction>
                      <Checkbox
                        checked={selectedFileOptions?.includes(item?.Option)}
                        onChange={() => toggleFileOption(item?.Option)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        }
        modalFooter={
          <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={() => setIsOpen(false)} size="small">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disableElevation
              size="small"
            >
              Save
            </Button>
          </Box>
        }
      />

      <ModalForms
        show={isEntityModalOpen}
        handleClose={() => setIsEntityModalOpen(false)}
        modalHeader={'Entity Data Setting'}
        size="lg"
        modalBody={
          <EntityDataSetting
            data={data}
            entitySetting={entitySetting}
            setEntitySetting={setEntitySetting}
            entitySettingErrors={entitySettingErrors}
          />
        }
        modalFooter={
          <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={() => setIsEntityModalOpen(false)} size="small">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveEntitySettings}
              size="small"
            >
              Save
            </Button>
          </Box>
        }
      />
      {isLeadModalOpen && (
        <ModalForms
          show={isLeadModalOpen}
          handleClose={() => setIsLeadModalOpen(false)}
          modalHeader={'Leads Data Setting'}
          size="lg"
          modalBody={
            <LeadDataSetting
              data={data}
              leadDataObj={leadDataObj}
              setLeadDataObj={setLeadDataObj}
              leadDataSettingErrors={leadDataSettingErrors}
            />
          }
          modalFooter={
            <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={() => setIsLeadModalOpen(false)} size="small">
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveLeadDataSettings}
                size="small"
              >
                Save
              </Button>
            </Box>
          }
        />
      )}
    </div>
  );
};

export default DynamicOptionsNode;
